import React, { useState } from 'react'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'

import { getUserFreeTrialDays } from 'utils/userAuth'
import { MIXPANEL_PROPS } from 'utils/mixpanel'
import { checkoutModalHandler } from 'utils/sendMessage'
import Button from 'components/ui/button'
import TWText from 'components/ui/text'
import { isDomain } from 'utils/url-validator'

import RankedCompetitors from '../tables/RankedCompetitors'
import { translate } from '../../utils/translation'
import { fonts, palette, mediaQueries } from '../../styles/variables'
import { filterNewCompetitors } from '../../utils/ptDataFromatting'
import {
  getMaxValuesForProject,
  getUserTier,
  isPaidUser,
} from '../../utils/freeVersionBlock'
import { RowContent } from '../modal/Modal'
import { getKeywordsCount } from '../../utils/functions'
import { LinkText, Text } from '../topography'
import { Desktop, Mobile } from '../../utils/media'
import DropDownSelector from '../selector/DropDownSelector'
import MultiLocationSelector from '../selector/MultiLocationSelector'
import NotePanel from '../panels/NotePanel'
import infoIcon from '../../styles/assets/info.svg'
import CompetitorTagInput from '../input/TagInput/CompetitorTagInput'

const getTotalCount = (competitors, newCompetitors, newLocations) =>
  getKeywordsCount(competitors || {}) +
  newLocations.length * newCompetitors.length

const Container = styled.div({})

const UberTabs = styled(Tabs)({
  '.react-tabs__tab-list': {
    borderBottom: `1px solid ${palette.lightGray}`,
    padding: '0',

    [mediaQueries.tablet]: {
      display: 'none',
    },

    '.react-tabs__tab': {
      padding: '8px 0',
      marginRight: '40px',
      border: 'none',
      borderBottom: `3px solid transparent`,
      color: palette.gray,
      bottom: '-2px',
      fontSize: '14px',
      lineHeight: '16px',
      fontFamily: fonts.primaryMedium,
      letterSpacing: '0.08em',

      '&:focus': {
        boxShadow: 'none',
        borderColor: palette.orange,

        ':after': {
          position: 'static',
        },
      },
    },

    '.react-tabs__tab--selected': {
      background: palette.white,
      borderBottom: `3px solid ${palette.orange}`,
      color: palette.darkGray,
      borderRadius: 0,
    },
  },

  '.react-tabs__tab-panel': {
    margin: '32px 0 0 0',
  },
})

const Title = styled.div({
  fontFamily: fonts.primaryMedium,
  fontSize: '14px',
  margin: '10px 0px',
})

const InnerContent = styled(RowContent)({
  [mediaQueries.mobile]: {
    flexWrap: 'wrap-reverse',
  },
})

const LocationSection = styled.div({
  width: '45%',
  marginRight: '24px',
  zIndex: 10,
  [mediaQueries.mobile]: {
    width: '100%',
    marginRight: 0,
    marginTop: '24px',
  },
})

const Content = styled(RowContent)((props) => ({
  gap: '15px',
  alignItems: 'flex-start',
  height: props.height || 'auto',
  [mediaQueries.laptop]: {
    flexWrap: 'wrap',
    height: 'auto',
  },
}))

const Column = styled.div(
  ({ width = '50%', justifyContent = 'flex-start' }) => ({
    display: 'flex',
    flexDirection: 'column',
    width,
    height: '100%',
    justifyContent,

    [mediaQueries.laptop]: {
      width: '100% !important',
      ':nth-child(2)': {
        marginTop: '24px',
      },
    },
  }),
)

const handleAddCompetitors = (newCompetitor, addedCompetitors) => {
  const filtered = filterNewCompetitors([newCompetitor], addedCompetitors)
  const newKeywordsToAdd = [...addedCompetitors, ...filtered]
  return newKeywordsToAdd
}

const RankedCompetitorsResponsive = ({ ...rest }) => {
  const isDesktop = useMediaQuery({ minDeviceWidth: 768 })
  return <RankedCompetitors {...rest} height={isDesktop ? '274px' : '150px'} />
}

export default ({
  competitors,
  project,
  domain,
  competitorsInProject,
  locations = [],
  pendingData,
  suggestedCompetitors,
  competitorsToAdd,
  setCompetitorsToAdd,
  selectedLocations,
  setSelectedLocations,
  areLocationsInvalid = false,
  newAddonProject,
  openEditLocationsModal,
}) => {
  const competitorsCount = getTotalCount(
    competitorsInProject,
    competitorsToAdd,
    selectedLocations,
  )
  const initialCount = getKeywordsCount(competitorsInProject)

  const { MAX_COMPETITORS_PER_PROJECT } = getMaxValuesForProject(
    project,
    newAddonProject,
  )
  const [tabIndex, setTabIndex] = useState(0)

  const onClickCTA = () => {
    const ctaType = isPaidUser()
      ? MIXPANEL_PROPS.BUTTON_CTA.values.UPGRADE
      : MIXPANEL_PROPS.BUTTON_CTA.values.START_TRIAL
    const mixpanelProps = [
      MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.ADD_COMPETITORS_MODAL,
      ctaType,
    ]

    checkoutModalHandler({ mixpanelProps })
  }

  const getCTAButton = (copyText) => (
    <Button
      onClick={onClickCTA}
      variant="legacy-link"
      className="!tw-px-0 tw-text-sm tw-normal-case tw-text-orange"
      isUppercase={false}
    >
      {translate(copyText)}
    </Button>
  )

  const ctaButton = isPaidUser()
    ? getCTAButton('upgrade_your_plan')
    : getCTAButton(translate('start_free_trial_button', getUserFreeTrialDays()))
  const upgradeText =
    getUserTier() === 'tier3'
      ? translate('can_track_up_to_competitors', MAX_COMPETITORS_PER_PROJECT)
      : translate(
          'cta_to_track_more_competitors',
          '{}',
          MAX_COMPETITORS_PER_PROJECT,
        )

  const tabs = [
    {
      value: 0,
      label: translate('search_engine_tab'),
    },
    {
      value: 1,
      label: translate('business_competitors_tab'),
    },
  ]

  return (
    <Container>
      <Mobile maxWidth={950}>
        <DropDownSelector
          options={tabs}
          value={tabIndex}
          onChange={setTabIndex}
          activeColor={palette.orange}
          arrowColor={palette.gray}
          borderColor={palette.lightGray}
        />
      </Mobile>
      <UberTabs
        selectedIndex={tabIndex}
        onSelect={(index) => setTabIndex(index)}
      >
        <TabList>
          <Tab>{translate('search_engine_tab')}</Tab>
          <Tab>{translate('business_competitors_tab')}</Tab>
        </TabList>

        <TabPanel>
          <Content>
            <Column>
              <RankedCompetitorsResponsive
                pendingData={pendingData}
                suggestedCompetitors={suggestedCompetitors}
                selectedCompetitors={competitorsToAdd}
                handleAddCompetitor={(newCompetitor) =>
                  setCompetitorsToAdd(
                    handleAddCompetitors(newCompetitor, competitorsToAdd),
                  )
                }
                width="100%"
              />
              <Desktop>
                <Title
                  data-testid="confirm-locations-label"
                  style={{
                    marginTop: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  {translate('confirm_locations')}
                  {openEditLocationsModal && (
                    <LinkText
                      textDecoration="underline"
                      onClick={() => openEditLocationsModal()}
                    >
                      {translate('edit_option')}
                    </LinkText>
                  )}
                </Title>
                <MultiLocationSelector
                  direction="top"
                  options={locations}
                  value={selectedLocations}
                  onChange={setSelectedLocations}
                  renderValue={(v) => `${v?.loc_id}_${v?.lang}`}
                  renderLabel={(v) => v?.name}
                />
                <Text
                  data-testid="confirm-locations-error"
                  fontSize="12px"
                  lineHeight="12px"
                  color={palette.danger}
                  margin="10px 0 0 0"
                >
                  {areLocationsInvalid
                    ? translate('no_locations_selected_error')
                    : ' '}
                </Text>
              </Desktop>
            </Column>
            <Column>
              <Title>{translate('competitors_added_to_project')}</Title>
              <Desktop>
                <CompetitorTagInput
                  competitors={competitors || {}}
                  domain={domain}
                  initialCount={initialCount}
                  tags={competitorsToAdd}
                  tagWeight={selectedLocations.length}
                  maxTags={MAX_COMPETITORS_PER_PROJECT}
                  removeOnBackspace
                  onChange={setCompetitorsToAdd}
                  placeholder={
                    competitorsToAdd.length === 0
                      ? translate('search_engine_tab_box2_placeholder_desktop')
                      : ' '
                  }
                  limitErrorMessageId={
                    selectedLocations.length > 1
                      ? 'reduce_competitors'
                      : 'competitor_limit_reached'
                  }
                  descriptionMessageId="track_more_competitors_in_x_locations"
                  height="330px"
                  validator={isDomain}
                />
              </Desktop>
              <Mobile>
                <CompetitorTagInput
                  competitors={competitors || {}}
                  domain={domain}
                  initialCount={initialCount}
                  tags={competitorsToAdd}
                  tagWeight={selectedLocations.length}
                  maxTags={MAX_COMPETITORS_PER_PROJECT}
                  removeOnBackspace
                  onChange={setCompetitorsToAdd}
                  placeholder={
                    competitorsToAdd.length === 0
                      ? translate(
                          'search_engine_tab_box2_placeholder_responsive',
                        )
                      : ' '
                  }
                  limitErrorMessageId={
                    selectedLocations.length > 1
                      ? 'reduce_competitors'
                      : 'competitor_limit_reached'
                  }
                  descriptionMessageId="track_more_competitors_in_x_locations"
                  height="330px"
                  validator={isDomain}
                />
              </Mobile>
              {selectedLocations.length > 1 && (
                <NotePanel
                  icon={infoIcon}
                  style={{ marginTop: '15px' }}
                  show={competitorsToAdd.length > 0}
                >
                  <Text
                    fontSize="14px"
                    lineHeight="20px"
                    color={palette.darkGray}
                    boldColor={palette.darkGray}
                    dangerouslySetInnerHTML={{
                      __html: translate(
                        'competitors_locations_added',
                        competitorsToAdd.length,
                        selectedLocations.length,
                        competitorsCount - initialCount,
                      ),
                    }}
                  />
                </NotePanel>
              )}

              <Mobile>
                <Title style={{ marginTop: '20px' }}>
                  {translate('confirm_locations')}
                </Title>
                <MultiLocationSelector
                  direction="top"
                  options={locations}
                  value={selectedLocations}
                  onChange={setSelectedLocations}
                  renderValue={(v) => `${v.loc_id}_${v.lang}`}
                  renderLabel={(v) => v.name}
                />
                <Text
                  fontSize="12px"
                  lineHeight="12px"
                  color={palette.danger}
                  margin="10px 0 0 0"
                >
                  {areLocationsInvalid
                    ? translate('no_locations_selected_error')
                    : ' '}
                </Text>
              </Mobile>
            </Column>
          </Content>
        </TabPanel>
        <TabPanel>
          <Content>
            <Column width="100%">
              <Title>{translate('search_engine_tab_label_box2')}</Title>
              <CompetitorTagInput
                competitors={competitors || {}}
                domain={domain}
                initialCount={initialCount}
                tags={competitorsToAdd}
                tagWeight={selectedLocations.length}
                maxTags={MAX_COMPETITORS_PER_PROJECT}
                removeOnBackspace
                onChange={setCompetitorsToAdd}
                placeholder={
                  competitorsToAdd.length === 0
                    ? translate('business_competitors_tab_placeholder')
                    : ' '
                }
                limitErrorMessageId={
                  selectedLocations.length > 1
                    ? 'reduce_competitors'
                    : 'competitor_limit_reached'
                }
                descriptionMessageId="track_more_competitors_in_x_locations"
                height="252px"
                validator={isDomain}
              />
              <InnerContent>
                <LocationSection>
                  <Title>{translate('confirm_locations')}</Title>
                  <MultiLocationSelector
                    direction="top"
                    options={locations}
                    value={selectedLocations}
                    onChange={setSelectedLocations}
                    renderValue={(v) => `${v.loc_id}_${v.lang}`}
                    renderLabel={(v) => v.name}
                  />
                  <Text
                    fontSize="12px"
                    lineHeight="12px"
                    color={palette.danger}
                    margin="10px 0 0 0"
                  >
                    {areLocationsInvalid
                      ? translate('no_locations_selected_error')
                      : ' '}
                  </Text>
                </LocationSection>
                {selectedLocations.length > 1 && (
                  <NotePanel icon={infoIcon} show={competitorsToAdd.length > 0}>
                    <Text
                      fontSize="14px"
                      lineHeight="20px"
                      color={palette.darkGray}
                      boldColor={palette.darkGray}
                      dangerouslySetInnerHTML={{
                        __html: translate(
                          'competitors_locations_added',
                          competitorsToAdd.length,
                          selectedLocations.length,
                          competitorsCount - initialCount,
                        ),
                      }}
                    />
                  </NotePanel>
                )}
              </InnerContent>
            </Column>
          </Content>
        </TabPanel>
      </UberTabs>
      <div className="tw-flex tw-justify-center">
        {getUserTier() !== 'tier3' && ctaButton}
        &nbsp;
        <TWText className="tw-text-sm">{upgradeText.replace('{}', ' ')}</TWText>
      </div>
    </Container>
  )
}
